import { createPortal } from 'preact/compat';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';

export interface SelectOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
  /** The heading it is listed under; options of one group come together. */
  readonly group?: string;
}

/** How far the list may reach, in rem; past it, it scrolls. */
const LIST_MAX_REM = 16;

interface Placement {
  readonly left: number;
  readonly width: number;
  readonly top?: number;
  readonly bottom?: number;
  readonly theme: string | null;
  readonly fontSize: string;
}

/**
 * The game's own drop-down (the user's rule, 2026-10-23: no control in the system's look, and no
 * focus left hanging on one). A button shows the choice; its list opens over everything, on the
 * paper or the glass of where it stands (`data-theme` of its trigger), below it or above when
 * there is no room. The keyboard walks it from the button (arrows, Home, End, Enter, Space, Esc)
 * and never reaches the world while it is open; a choice made with the mouse gives the focus back
 * to the game, so the keys of movement and action are the world's again at once.
 */
export function UiSelect<T extends string>(props: {
  readonly value: T;
  readonly options: readonly SelectOption<T>[];
  readonly onChange: (value: T) => void;
  readonly label: string;
  readonly testId?: string;
  readonly disabled?: boolean;
  readonly class?: string;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<Placement | null>(null);
  const chosen = props.options.find((option) => option.value === props.value);

  const enabled = (index: number) => props.options[index]?.disabled !== true;
  const step = (from: number, by: 1 | -1): number => {
    for (let i = from + by; i >= 0 && i < props.options.length; i += by) if (enabled(i)) return i;
    return from;
  };

  const show = () => {
    const at = button.current;
    if (!at || props.disabled) return;
    const rect = at.getBoundingClientRect();
    const style = getComputedStyle(at);
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const reach = Math.min(LIST_MAX_REM * rem, props.options.length * 2.4 * rem);
    const below = innerHeight - rect.bottom;
    const up = below < reach && rect.top > below;
    setPlace({
      left: rect.left,
      width: rect.width,
      ...(up ? { bottom: innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      theme: at.closest('[data-theme]')?.getAttribute('data-theme') ?? null,
      fontSize: style.fontSize,
    });
    const index = props.options.findIndex((option) => option.value === props.value);
    setActive(index >= 0 ? index : step(-1, 1));
    setOpen(true);
    // Said at once, before the press that opened it ends: the game leaves an open list its focus.
    at.setAttribute('aria-expanded', 'true');
  };

  const close = (backToGame: boolean) => {
    setOpen(false);
    if (backToGame) button.current?.blur();
  };

  const choose = (index: number, backToGame: boolean) => {
    const option = props.options[index];
    if (!option || option.disabled) return;
    close(backToGame);
    if (option.value !== props.value) props.onChange(option.value);
  };

  // A press anywhere else, a scroll or a resize closes it (its place would be stale).
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      const target = event.target as Node | null;
      if (target && (button.current?.contains(target) || list.current?.contains(target))) return;
      close(false);
    };
    const moved = (event: Event) => {
      if (list.current?.contains(event.target as Node)) return;
      close(false);
    };
    const resized = () => {
      close(false);
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('scroll', moved, true);
    globalThis.addEventListener('resize', resized);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('scroll', moved, true);
      globalThis.removeEventListener('resize', resized);
    };
  }, [open]);

  // The active option stays in sight as the keys walk the list.
  useLayoutEffect(() => {
    if (open)
      list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onKeyDown = (event: KeyboardEvent) => {
    const keys = open
      ? ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Escape', 'Tab']
      : ['ArrowDown', 'ArrowUp', 'Enter', ' '];
    if (!keys.includes(event.key)) return;
    // Nothing the list takes reaches the world's keys or the panels' shortcuts.
    event.stopPropagation();
    if (event.key !== 'Tab') event.preventDefault();
    if (!open) {
      show();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        setActive((index) => step(index, 1));
        break;
      case 'ArrowUp':
        setActive((index) => step(index, -1));
        break;
      case 'Home':
        setActive(step(-1, 1));
        break;
      case 'End':
        setActive(step(props.options.length, -1));
        break;
      case 'Enter':
      case ' ':
        choose(active, false);
        break;
      default:
        close(false);
    }
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        class={`ui-select${props.class ? ` ${props.class}` : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={props.label}
        aria-activedescendant={open ? `${id}-${String(active)}` : undefined}
        data-testid={props.testId}
        data-value={props.value}
        disabled={props.disabled}
        onClick={(event) => {
          if (open) close(event.detail > 0);
          else show();
        }}
        onKeyDown={onKeyDown}
      >
        <span class="ui-select__value">{chosen?.label ?? ''}</span>
        <span class="ui-select__chevron" aria-hidden="true" />
      </button>
      {open &&
        place &&
        createPortal(
          <ul
            ref={list}
            id={id}
            role="listbox"
            class="ui-pop ui-select__list"
            aria-label={props.label}
            data-theme={place.theme ?? undefined}
            style={{
              left: `${String(place.left)}px`,
              minWidth: `${String(place.width)}px`,
              fontSize: place.fontSize,
              ...(place.top !== undefined && { top: `${String(place.top)}px` }),
              ...(place.bottom !== undefined && { bottom: `${String(place.bottom)}px` }),
            }}
          >
            {props.options.map((option, index) => [
              option.group !== undefined && option.group !== props.options[index - 1]?.group && (
                <li key={`group-${option.group}`} role="presentation" class="ui-select__group">
                  {option.group}
                </li>
              ),
              <li
                key={option.value}
                id={`${id}-${String(index)}`}
                role="option"
                class="ui-select__option"
                aria-selected={option.value === props.value}
                aria-disabled={option.disabled}
                data-active={index === active}
                data-value={option.value}
                onPointerEnter={() => {
                  if (!option.disabled) setActive(index);
                }}
                // The button keeps the focus while the list is open.
                onPointerDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  choose(index, true);
                }}
              >
                {option.label}
              </li>,
            ])}
          </ul>,
          document.body,
        )}
    </>
  );
}
